<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { onMounted } from "vue";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import type { CloseEvent } from "~/core/models/CloseEvent.interface";
import { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import type SectorToolbar from "~/modules/sector/components/SectorToolbar.vue";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import { useFetchUsers } from "~/modules/user/composables/useFetchUsers";
import type { User } from "~/modules/user/models/User.interface";
import { getProfileLabel } from "~/modules/user/utils/profileLabel";
import { SectorResource } from "~/types";

const props = defineProps<{
  sector: Sector;
}>();

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const currentSelectedUser: Ref<string | undefined> = ref(undefined);
const {
  isLoading,
  results: users,
  error,
  fetchData,
  canManageItems,
  getDescription,
} = useFetchUsers({
  organizationCode: currentOrganization.value?.code,
  sectorCode: props.sector.code,
});

const selectedItems: Ref<User[]> = ref([]);
const displayActions = computed(() => canManageItems(props.sector));

const sectorToolbarRef: Ref<typeof SectorToolbar | undefined> = ref();
const showUserForm: Ref<boolean> = ref(false);

onMounted(fetchSectorUsers);

async function fetchSectorUsers(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  const params = new Map();
  await fetchData(page, pageSize, params);
}

async function refresh() {
  await fetchSectorUsers();
  selectedItems.value = [];
}

function onRowClick(user: User) {
  showUserForm.value = true;
  currentSelectedUser.value = user.login;
}

function onClose(event: CloseEvent) {
  if (event.edited) {
    refresh();
  }
}
</script>

<template>
  <sector-toolbar
    ref="sectorToolbarRef"
    primary-key="login"
    :selected-items="selectedItems"
    :sector="sector"
    :resource="SectorResource.USER"
    @change="refresh"
  >
    <lxc-table-column prop="lastName" :label="t('table.header.lastname')" />
    <lxc-table-column prop="firstName" :label="t('table.header.firstname')" />
    <lxc-table-column :label="t('table.header.profile')" width="12rem">
      <template #default="scope">
        <badge-for-list
          :label="getProfileLabel(scope.row.profile)"
          type="secondary"
        />
      </template>
    </lxc-table-column>
  </sector-toolbar>

  <container-component
    :px="0"
    :py="0"
    class="mt-4"
    :is-loading="isLoading && !users"
    :error="error"
  >
    <sectors-details-table
      v-model="selectedItems"
      primary-key="login"
      :is-loading="isLoading"
      :selection="displayActions"
      :items="users"
      :display-actions="displayActions"
      :resource="SectorResource.USER"
      :get-description="getDescription"
      @pagination:get-next-page="fetchSectorUsers"
      @actions:detach="(item) => $refs.sectorToolbarRef.openDetachModal([item])"
      @row-click="onRowClick"
    >
      <lxc-table-column
        prop="lastName"
        :label="t('table.header.lastname')"
        min-width="20rem"
      />
      <lxc-table-column
        prop="firstName"
        :label="t('table.header.firstname')"
        min-width="20rem"
      />
      <lxc-table-column :label="t('table.header.profile')" width="15rem">
        <template #default="scope">
          <badge-for-list
            v-if="scope.row.profile"
            :label="getProfileLabel(scope.row.profile)"
            type="secondary"
          />
        </template>
      </lxc-table-column>
    </sectors-details-table>
  </container-component>
  <user-form
    v-model:show="showUserForm"
    :organization-code="currentOrganization?.code"
    :organization-type="OrganizationType.OPERATOR"
    :user="currentSelectedUser"
    @close="onClose"
  ></user-form>
</template>
